// FILE: src/components/EnhancedKYBInterface.tsx
// UPDATED: Improved tab implementation with consistent layout and dynamic headers

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { streamBusinessVerification, type CompanyInfo } from '../api/streaming';
import { Users, AlertTriangle, Building2, FileText } from 'lucide-react';

// Types
import type { ApiResult, SystemStatus } from '../types/kyb.types';

// Layout
import { Header } from './layout/Header';
import { AIBackground } from './layout/AIBackground';

// UI Components
import { ProgressBar } from './ui/ProgressBar';
import { EmptyStateCard } from './ui/EmptyStateCard';

// Forms
import { AnalysisForm } from './kyb/forms/AnalysisForm';

// Display
import { StatusBanner, ScoreGrid, SignalsDisplay } from './kyb/display/index';

// Sections
import { CompaniesHouseSection, BusinessProfileSection } from './kyb/sections/index';
import { TabbedNavigation } from './kyb/sections/TabbedNavigation';

// Analysis
import { EnhancedAIAnalysis, ReasoningMetadataCard } from './kyb/analysis/index';

// Warnings
import { CompanyConflictWarning } from './kyb/warnings/index';

import { FooterMetadata } from './kyb/FooterMetadata';
import { OfficersDisplay } from "./kyb/display/OfficersDisplay";

const EnhancedKYBInterface: React.FC = () => {
  const { getToken } = useAuth();
  const [businessName, setBusinessName] = useState<string>('');
  const [country, setCountry] = useState<string>('GB');
  const [entityType, setEntityType] = useState<string>('any');
  const [website, setWebsite] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [results, setResults] = useState<ApiResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [analysisProgress, setAnalysisProgress] = useState<number>(0);
  const [progressMessage, setProgressMessage] = useState<string>('');
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [activeTab, setActiveTab] = useState<string>('overview');

  const isFetchingStatus = useRef<boolean>(false);
  const cancelAnalysisRef = useRef<(() => void) | null>(null);

  // Calculate counts for tab badges
  const officersCount = results?.companies_house_data?.officers?.length || 0;
  const signalsCount = (results?.positive_signals?.length || 0) + (results?.negative_signals?.length || 0);
  const hasCompanyData = !!(results?.companies_house_data || results?.business_profile);

  useEffect(() => {
    console.log('🚨 REACT STATE UPDATED');
    console.log('  - results exists:', !!results);
    console.log('  - companies_house_data:', !!results?.companies_house_data);
    if (results?.companies_house_data) {
      console.log('  - officers in state:', results.companies_house_data.officers?.length, 'officers');
      console.log('  - officersCount computed:', officersCount);
    }
    console.log('  - positive_signals:', results?.positive_signals?.length);
    console.log('  - negative_signals:', results?.negative_signals?.length);
    console.log('  - signalsCount computed:', signalsCount);
  }, [results, officersCount, signalsCount]);

  // Reset to overview tab when new analysis is performed
  useEffect(() => {
    if (isAnalyzing) {
      setActiveTab('overview');
    }
  }, [isAnalyzing]);

  const handleCancelAnalysis = () => {
    console.log('[UI] Cancel button clicked');
    if (cancelAnalysisRef.current) {
      cancelAnalysisRef.current();
      cancelAnalysisRef.current = null;
    }
    setIsAnalyzing(false);
    setAnalysisProgress(0);
    setProgressMessage('Analysis cancelled');
    setError('Analysis was cancelled by user');
  };

  const handleAnalysis = async () => {
    if (!businessName.trim()) {
      setError('Please enter a business name');
      return;
    }

    setError(null);
    setIsAnalyzing(true);
    setResults(null);
    setAnalysisProgress(0);
    setProgressMessage('Initializing analysis...');

    try {
      // Get authentication token
      const token = await getToken();
      
      if (!token) {
        setError('Authentication required. Please sign in again.');
        setIsAnalyzing(false);
        return;
      }

      const companyInfo: CompanyInfo = {
        company_name: businessName.trim(),
        country: country || 'GB',
        entity_type: entityType || 'any',
        website: website.trim() || undefined
      };

      const cancelFn = streamBusinessVerification(
        companyInfo,
        (progressData) => {
          if (progressData.progress !== undefined) {
            setAnalysisProgress(progressData.progress);
          }
          if (progressData.message) {
            setProgressMessage(progressData.message);
          }
        },
        (result) => {
          console.log('🚨 REACT: Result received, setting to state');
          console.log('  - companies_house_data:', !!result?.companies_house_data);
          if (result?.companies_house_data) {
            console.log('  - officers:', result.companies_house_data.officers?.length, 'officers');
          }
          console.log('  - positive_signals:', result?.positive_signals?.length);
          console.log('  - negative_signals:', result?.negative_signals?.length);
          
          setResults(result);
          setAnalysisProgress(100);
          setProgressMessage('Analysis complete!');
          cancelAnalysisRef.current = null;
          setTimeout(() => {
            setIsAnalyzing(false);
          }, 500);
        },
        (err) => {
          setError(err?.message ?? 'Analysis failed');
          setIsAnalyzing(false);
          cancelAnalysisRef.current = null;
        },
        token  // Pass auth token
      );
      
      // Store the cancel function
      cancelAnalysisRef.current = cancelFn;
    } catch (err: any) {
      setError(err?.message ?? 'Analysis failed');
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <AIBackground />
      
      <div className="relative z-10">
        <Header systemStatus={systemStatus} />

        <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-5xl mx-auto">
            <AnalysisForm
              businessName={businessName}
              setBusinessName={setBusinessName}
              country={country}
              setCountry={setCountry}
              entityType={entityType}
              setEntityType={setEntityType}
              website={website}
              setWebsite={setWebsite}
              isAnalyzing={isAnalyzing}
              onAnalyze={handleAnalysis}
            />

            {isAnalyzing && (
              <ProgressBar 
                progress={analysisProgress}
                message={progressMessage}
                onCancel={handleCancelAnalysis}
                className="mt-8"
              />
            )}

            {error && !isAnalyzing && (
              <div className="mt-8 p-6 bg-red-50 border-2 border-red-200 rounded-xl shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-red-800 mb-2">Analysis Error</h3>
                    <p className="text-red-700 mb-3">{error}</p>
                    
                    {/* Show helpful link for quota errors */}
                    {(error.toLowerCase().includes('quota') || error.toLowerCase().includes('billing')) && (
                      <a 
                        href="https://platform.openai.com/account/billing"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Add Credits to OpenAI Account
                      </a>
                    )}
                    
                    {/* Show helpful link for rate limit errors */}
                    {error.toLowerCase().includes('rate limit') && !error.toLowerCase().includes('quota') && (
                      <p className="text-sm text-red-600 mt-2">
                        ⏱️ Please wait a moment and try again.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {results && !isAnalyzing && (
              <div className="mt-8 space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                {/* Always Visible Components */}
                {results.companies_house_data?.compliance_analysis && (
                  <StatusBanner complianceAnalysis={results.companies_house_data.compliance_analysis} />
                )}

                {results.company_conflict && (
                  <CompanyConflictWarning conflict={results.company_conflict} />
                )}

                <ScoreGrid results={results} />

                {/* Improved Tabbed Navigation with counts */}
                <TabbedNavigation 
                  activeTab={activeTab} 
                  setActiveTab={setActiveTab}
                  officersCount={officersCount}
                  signalsCount={signalsCount}
                  hasCompanyData={hasCompanyData}
                />

                {/* Tab Content */}
                <div className="space-y-6">
                  {/* Overview Tab */}
                  {activeTab === 'overview' && (
                    <>
                      <EnhancedAIAnalysis 
                        llmInsights={results.llm_insights} 
                        reasoning={results.reasoning} 
                      />
                      
                      {results.reasoning_metadata && (
                        <ReasoningMetadataCard metadata={results.reasoning_metadata} />
                      )}
                    </>
                  )}

                  {/* Company Details Tab */}
                  {activeTab === 'company' && (
                    <>
                      {results.companies_house_data ? (
                        <CompaniesHouseSection companiesHouseData={results.companies_house_data} />
                      ) : (
                        <EmptyStateCard
                          icon={Building2}
                          title="Companies House Data"
                          description="No Companies House registration data is available for this company. This may indicate an international company or one not registered in the UK."
                          gradientFrom="from-blue-600"
                          gradientTo="to-purple-700"
                        />
                      )}
                      
                      {results.business_profile ? (
                        <BusinessProfileSection 
                          businessProfile={results.business_profile}
                          businessProfileQuality={results.business_profile_quality}
                        />
                      ) : (
                        <EmptyStateCard
                          icon={FileText}
                          title="Business Profile"
                          description="No web-sourced business information could be found for this company. The company may have limited online presence."
                          gradientFrom="from-slate-700"
                          gradientTo="to-slate-900"
                        />
                      )}
                    </>
                  )}

                  {/* Officers Tab */}
                  {activeTab === 'officers' && (
                    <>
                      {officersCount > 0 ? (
                        <OfficersDisplay 
                          officers={results.companies_house_data!.officers}
                          companyNumber={results.companies_house_data?.company_number || undefined}
                        />
                      ) : (
                        <EmptyStateCard
                          icon={Users}
                          title="Company Officers"
                          description="No officer information is available for this company. Officer data may not be publicly available or the company structure may not require registered officers."
                          gradientFrom="from-indigo-600"
                          gradientTo="to-purple-600"
                          iconColor="text-indigo-400"
                        />
                      )}
                    </>
                  )}

                  {/* Risk Signals Tab */}
                  {activeTab === 'signals' && (
                    <>
                      {signalsCount > 0 ? (
                        <SignalsDisplay 
                          positiveSignals={results.positive_signals || []}
                          negativeSignals={results.negative_signals || []}
                        />
                      ) : (
                        <EmptyStateCard
                          icon={AlertTriangle}
                          title="Risk Signals"
                          description="No specific risk indicators or positive signals were identified during the analysis. This indicates a neutral risk profile based on available data."
                          gradientFrom="from-amber-600"
                          gradientTo="to-orange-700"
                          iconColor="text-amber-400"
                        />
                      )}
                    </>
                  )}
                </div>

                {/* Footer - Always Visible */}
                <FooterMetadata 
                  analysisMethod={results.analysis_method}
                  version={results.version}
                  timestamp={results.analysis_timestamp}
                  results={results}
                  getToken={getToken}
                />
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default EnhancedKYBInterface;